import { useEffect, useState } from "react";
import { AxiosError } from "axios";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { confirmQrRefund, requestQrRefund } from "../../services";
import { formatDateTimeBkk } from "../../utils/date";
import { QrPaymentListItem, settledAmountOf } from "../../types/qr-payment.type";

/**
 * The server answers in English, because its messages are read by developers and by other
 * services as much as by this screen. Mapped here rather than translated on the server so
 * one refusal can read differently in the two places it can arrive — a queue screen where
 * the operator chose the row, and an API response.
 *
 * Matched on the exact server string. An unmapped message falls through to itself, which is
 * worse-looking than Thai but still true; inventing a friendly Thai message for a refusal we
 * did not anticipate would be the failure mode that costs someone real money.
 */
const REFUSAL_TH: Record<string, string> = {
  "A refund cannot be ordered for this QR payment. Either the bank never confirmed money against it, it has already been refunded, or a refund has already been ordered.":
    "รายการนี้สั่งคืนเงินไม่ได้ อาจเพราะยังไม่มีเงินเข้า คืนไปแล้ว หรือสั่งคืนไปแล้ว กรุณารีเฟรชแล้วดูใหม่",
  "A refund must be ordered for this QR payment before it can be confirmed.":
    "ต้องกด “สั่งคืนเงิน” ก่อน จึงจะยืนยันได้",
  "Failed to record the refund request for this QR payment.":
    "สั่งคืนเงินไม่สำเร็จ กรุณาลองใหม่",
  "Failed to record the refund for this QR payment.":
    "ยืนยันคืนเงินไม่สำเร็จ กรุณาลองใหม่",
};

const refusalText = (error: unknown, fallback: string): string => {
  const message = (error as AxiosError<{ message?: string }>)?.response?.data?.message;
  if (!message) return fallback;
  return REFUSAL_TH[message] ?? message;
};

const baht = (value: number): string =>
  value.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export type RefundDialogMode = "request" | "confirm";

interface RefundDialogProps {
  row: QrPaymentListItem | null;
  mode: RefundDialogMode;
  onClose: () => void;
  onDone: () => void;
}

/**
 * The two halves of a manual refund: ordering one, and recording that it happened.
 *
 * One component rather than two because the operator is looking at the same row and needs the
 * same facts restated — the amount, the payer, the reference — to recognise a wrong row before
 * committing. What differs is which half of the job is being closed.
 *
 * Neither half moves money. The platform no longer reverses payments at all: ordering a refund
 * writes a work order for accounting, who transfer the money back in the bank's own system,
 * then come back here to confirm it. Both halves are one-way, which is why each restates what
 * it is about to do rather than relying on the operator having read the row.
 *
 * The refusal is shown HERE rather than as a toast. A toast that appears while the dialog is
 * still open, over a list of near-identical rows, does not say which row it is about.
 */
export default function RefundDialog({
  row,
  mode,
  onClose,
  onDone,
}: Readonly<RefundDialogProps>) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");

  // A previous row's refusal, or a previous row's typing, must not greet whoever opens the
  // dialog next.
  useEffect(() => {
    setError(null);
    setReason("");
    setReference("");
  }, [row, mode]);

  const isConfirm = mode === "confirm";
  const trimmedReference = reference.trim();

  const handleSubmit = async () => {
    if (!row) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isConfirm) {
        await confirmQrRefund(row.uuid, trimmedReference, reason.trim() || undefined);
      } else {
        await requestQrRefund(row.uuid, reason.trim() || undefined);
      }
      onDone();
    } catch (caught) {
      setError(
        refusalText(
          caught,
          isConfirm ? "ยืนยันคืนเงินไม่สำเร็จ กรุณาลองใหม่" : "สั่งคืนเงินไม่สำเร็จ กรุณาลองใหม่",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={Boolean(row)} onClose={submitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isConfirm ? "ยืนยันว่าคืนเงินแล้ว" : "สั่งคืนเงินลูกค้า"}</DialogTitle>
      <DialogContent>
        {row && (
          <Stack spacing={1.5}>
            {isConfirm ? (
              <Alert severity="error">
                ยืนยันแล้วแก้ไขไม่ได้ และระบบจะไม่ตรวจสอบรายการนี้กับธนาคารอีกเลย
                กรุณายืนยันเฉพาะเมื่อโอนเงินคืนลูกค้าเรียบร้อยแล้วจริง
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                ระบบจะบันทึกว่ารายการนี้ต้องคืนเงินลูกค้า แต่จะไม่ติดต่อธนาคารให้
                บัญชีต้องเป็นผู้โอนคืนเอง แล้วกลับมากดยืนยันพร้อมเลขอ้างอิง
                รายการที่สั่งคืนแล้วยกเลิกไม่ได้ และจะนำไปผูกกับบิลใหม่ไม่ได้อีก
              </Typography>
            )}

            <Divider />

            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2">ยอดที่ต้องคืน</Typography>
              <Typography variant="h6" color="error.main">
                {baht(settledAmountOf(row))} บาท
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Typography variant="body2">ผู้โอน</Typography>
              <Typography variant="body2">{row.payer_name ?? "—"}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Typography variant="body2">เวลาเงินเข้า</Typography>
              <Typography variant="body2">
                {formatDateTimeBkk(row.paid_at ?? row.confirmation_received_at) || "—"}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Typography variant="body2">อ้างอิง (ref1)</Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {row.ref1}
              </Typography>
            </Stack>

            {/* Who ordered it and why, restated to whoever is closing the job — they are
                usually not the person who raised it, and days may have passed. */}
            {isConfirm && row.refund_requested_at && (
              <>
                <Divider />
                <Stack direction="row" justifyContent="space-between" gap={2}>
                  <Typography variant="body2">สั่งคืนเมื่อ</Typography>
                  <Typography variant="body2">
                    {formatDateTimeBkk(row.refund_requested_at) || "—"}
                    {row.refund_requested_by ? " · " + row.refund_requested_by : ""}
                  </Typography>
                </Stack>
                {row.refund_reason && (
                  <Stack direction="row" justifyContent="space-between" gap={2}>
                    <Typography variant="body2">เหตุผล</Typography>
                    <Typography variant="body2" sx={{ textAlign: "right" }}>
                      {row.refund_reason}
                    </Typography>
                  </Stack>
                )}
              </>
            )}

            {isConfirm && (
              <TextField
                label="เลขอ้างอิงการคืนเงินจากธนาคาร"
                required
                size="small"
                value={reference}
                disabled={submitting}
                onChange={(event) => setReference(event.target.value)}
                helperText="เลขอ้างอิง/เลขที่สลิปจากธนาคาร — เป็นหลักฐานชิ้นเดียวที่ระบบจะเก็บไว้"
              />
            )}

            <TextField
              label={isConfirm ? "หมายเหตุ (ถ้ามี)" : "เหตุผลที่ต้องคืนเงิน (ถ้ามี)"}
              size="small"
              multiline
              minRows={2}
              value={reason}
              disabled={submitting}
              onChange={(event) => setReason(event.target.value)}
            />

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          ยกเลิก
        </Button>
        <Button
          onClick={handleSubmit}
          color="error"
          variant="contained"
          loading={submitting}
          // The reference is the whole point of the confirm half — there is nothing else
          // behind the claim that the money moved — so an empty one cannot be submitted.
          disabled={isConfirm && trimmedReference === ""}
        >
          {isConfirm ? "ยืนยันคืนเงินแล้ว" : "สั่งคืนเงิน"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
